import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils";
import { useState } from "react";
import {
  CanvasToolbarButton,
  CanvasToolbarGroup,
  CanvasToolbarShell,
} from "../../components/toolbar";
import { canvasToolbarButtonVariants } from "../../components/toolbar/canvasToolbarStyles";
import type { CanvasColor } from "./CanvasColorPalettes";
import { CANVAS_NO_FILL_VALUE } from "./CanvasColorPalettes";
import { CanvasToolbarColorSwatch } from "./CanvasToolbarGraphics";

function isNoFillSwatch(color: CanvasColor): boolean {
  return color.value === CANVAS_NO_FILL_VALUE;
}

interface CanvasColorPickerButtonProps {
  colors: CanvasColor[];
  /** Currently selected color (matches CanvasColor.value or .swatchHex). */
  activeValue: string;
  tooltip?: string;
  onColorChange: (value: string) => void;
  /**
   * When set, the transparent swatch toggles fill independently of `activeValue`
   * (shapes/sections: color drives border; no-fill only hides the fill).
   */
  noFill?: boolean;
  onNoFillChange?: (noFill: boolean) => void;
  onOpenChange?: (open: boolean) => void;
  /** Optional header rendered above the color swatches in the popover */
  renderPopoverHeader?: () => React.ReactNode;
  /** Which side the color picker popover opens toward. Defaults to "top". */
  popoverSide?: "top" | "bottom" | "left" | "right";
  /** Distance in px between the trigger and the popover. Defaults to 8. */
  popoverSideOffset?: number;
  /** Swatch row layout inside the popover. Primary toolbar flyouts use vertical. */
  swatchOrientation?: "horizontal" | "vertical";
  /** Optional class on the swatch trigger (primary flyout menus use h-9 w-9). */
  triggerClassName?: string;
  /**
   * Which field to emit via onColorChange when a swatch is clicked.
   * "value"    — emits color.value (default; used by shapes which store the tint fill).
   * "swatchHex"— emits color.swatchHex (used by connectors/edges which store the saturated stroke).
   */
  emitField?: "value" | "swatchHex";
}

/**
 * Swatch fill/ring for one palette entry — shared with inline swatch rows.
 * The design-system recipe (specs/canvas-toolbars ColorRow): centre = the
 * light fill tint, ring = the paired dark stroke. Entries without an explicit
 * pair (theme neutrals) get a soft tint of their own colour as the centre, the
 * way the design system draws its grey and white swatches.
 */
export function canvasSwatchColors(color: CanvasColor): {
  fill: string;
  stroke: string;
} {
  if (isNoFillSwatch(color)) {
    return { fill: color.value, stroke: color.swatchHex };
  }
  return {
    fill:
      color.swatchFill ??
      `color-mix(in oklab, ${color.swatchHex} 22%, var(--n-00))`,
    stroke: color.swatchStroke ?? color.swatchHex,
  };
}

/**
 * Reusable color picker swatch button + popover.
 * Color row matches specs/canvas-toolbars ColorRow (Type-1 toolbar submenu).
 */
export function CanvasColorPickerButton({
  colors,
  activeValue,
  tooltip = "Change color",
  onColorChange,
  noFill = false,
  onNoFillChange,
  onOpenChange,
  renderPopoverHeader,
  popoverSide = "top",
  popoverSideOffset = 8,
  swatchOrientation = "horizontal",
  triggerClassName,
  emitField = "value",
}: CanvasColorPickerButtonProps) {
  // Match on value first (shapes store the tint), then swatchHex (connectors/edges
  // store the saturated color directly). Falls back to null when no match.
  const current =
    colors.find((c) => c.value === activeValue) ??
    colors.find((c) => c.swatchHex === activeValue) ??
    null;
  const triggerSwatch = current
    ? canvasSwatchColors(current)
    : { fill: activeValue, stroke: activeValue };
  const [isOpen, setIsOpen] = useState(false);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    onOpenChange?.(open);
  };

  const colorRow = (
    <CanvasToolbarShell
      orientation={swatchOrientation}
      aria-label="Color"
      className={cn(
        "max-w-none",
        renderPopoverHeader && "border-0 p-0 shadow-none",
      )}
    >
      <CanvasToolbarGroup orientation={swatchOrientation}>
        {colors.map((color) => {
          const { fill, stroke } = canvasSwatchColors(color);

          if (isNoFillSwatch(color) && onNoFillChange) {
            return (
              <CanvasToolbarButton
                key={color.value}
                label={color.name}
                active={noFill}
                onClick={() => {
                  onNoFillChange(!noFill);
                }}
              >
                <CanvasToolbarColorSwatch fill={fill} stroke={stroke} />
              </CanvasToolbarButton>
            );
          }

          const isActive =
            activeValue === color.value || activeValue === color.swatchHex;

          return (
            <CanvasToolbarButton
              key={color.value}
              label={color.name}
              active={isActive}
              onClick={() => {
                onColorChange(
                  emitField === "swatchHex" ? color.swatchHex : color.value,
                );
                setIsOpen(false);
                onOpenChange?.(false);
              }}
            >
              <CanvasToolbarColorSwatch fill={fill} stroke={stroke} />
            </CanvasToolbarButton>
          );
        })}
      </CanvasToolbarGroup>
    </CanvasToolbarShell>
  );

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={tooltip}
              className={cn(canvasToolbarButtonVariants(), triggerClassName)}
              onClick={(e) => e.stopPropagation()}
            >
              <CanvasToolbarColorSwatch
                fill={triggerSwatch.fill}
                stroke={triggerSwatch.stroke}
              />
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="top">{tooltip}</TooltipContent>
      </Tooltip>
      <PopoverContent
        className="w-auto border-0 bg-transparent p-0 shadow-none"
        align="center"
        side={popoverSide}
        sideOffset={popoverSideOffset}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        {renderPopoverHeader ? (
          <div className="flex flex-col gap-2 rounded-lg border border-n-10 bg-n-00 p-2 shadow-elev-5">
            {renderPopoverHeader()}
            {colorRow}
          </div>
        ) : (
          colorRow
        )}
      </PopoverContent>
    </Popover>
  );
}
